import React, { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  requestOtp,
  verifyOtp,
  fetchHistory,
  fetchUpcomingReservations,
  fetchUpcomingEventOrders,
  createGuestRequest,
  fetchMyRequests,
  fetchTransferTargets,
  cancelMyRequest,
  fetchRescheduleSlots,
  fetchRestaurantBySlug,
  getPortalToken,
  getPortalEmail,
  clearPortalSession,
} from "../../services/guestPortalService";
import "./GuestPortal.css";

const STEP_EMAIL = "email";
const STEP_OTP = "otp";
const STEP_DASHBOARD = "dashboard";

const OTP_LENGTH = 6;

// Restaurant scope is shared down the component tree so nested forms/modals
// don't need slug or id threaded through every prop.
const PortalContext = createContext({ slug: null, restaurant: null });
const usePortal = () => useContext(PortalContext);

export default function GuestPortal() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";
  const sessionExpired = searchParams.get("session") === "expired";

  const [restaurant, setRestaurant] = useState(null);
  const [restaurantError, setRestaurantError] = useState("");
  const [loadingRestaurant, setLoadingRestaurant] = useState(true);

  const [step, setStep] = useState(
    getPortalToken(slug) ? STEP_DASHBOARD : STEP_EMAIL,
  );
  const [email, setEmail] = useState(getPortalEmail(slug) || initialEmail);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState(
    sessionExpired ? "Your portal session expired. Please verify your email to continue." : "",
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingRestaurant(true);
    setRestaurantError("");
    fetchRestaurantBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        setRestaurant(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setRestaurantError(
          err?.response?.status === 404
            ? "This portal link is invalid or the restaurant is no longer available."
            : err?.response?.data?.message || err.message || "Failed to load restaurant.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingRestaurant(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onRequestOtp = async (e) => {
    e?.preventDefault?.();
    setError("");
    setInfo("");
    if (!email) return setError("Please enter your email.");
    if (!restaurant?.id) return setError("Restaurant not loaded.");
    setBusy(true);
    try {
      await requestOtp(email, restaurant.id);
      setInfo("We sent a 6-digit code to your email. Check your inbox.");
      setStep(STEP_OTP);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to send code.");
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (codeOverride) => {
    setError("");
    const code = codeOverride ?? otp;
    if (!code || code.length < OTP_LENGTH) return setError("Please enter the full 6-digit code.");
    if (!restaurant?.id) return setError("Restaurant not loaded.");
    setBusy(true);
    try {
      await verifyOtp(slug, email, code, restaurant.id);
      setInfo("");
      setStep(STEP_DASHBOARD);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    clearPortalSession(slug);
    setOtp("");
    setStep(STEP_EMAIL);
  };

  if (loadingRestaurant) {
    return (
      <div className="gp-page">
        <div className="gp-wrap">
          <div className="gp-card gp-card-narrow">
            <div className="gp-card-body" style={{ textAlign: "center" }}>
              <span className="spinner-border" role="status" aria-hidden="true"></span>
              <p style={{ marginTop: 16 }}>Loading…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (restaurantError || !restaurant) {
    return (
      <div className="gp-page">
        <div className="gp-wrap">
          <div className="gp-card gp-card-narrow">
            <div className="gp-card-body" style={{ textAlign: "center" }}>
              <i className="bi bi-exclamation-triangle" style={{ fontSize: 32, color: "#c62828" }}></i>
              <h2 className="gp-title" style={{ marginTop: 12 }}>Portal unavailable</h2>
              <p className="gp-sub">{restaurantError || "Restaurant not found."}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PortalContext.Provider value={{ slug, restaurant }}>
      <div className="gp-page">
        <div className="gp-wrap">
          <BrandBar />
          {step === STEP_EMAIL && (
            <EmailStep
              email={email}
              setEmail={setEmail}
              onSubmit={onRequestOtp}
              busy={busy}
              error={error}
              info={info}
            />
          )}
          {step === STEP_OTP && (
            <OtpStep
              email={email}
              otp={otp}
              setOtp={setOtp}
              onSubmit={() => onVerify()}
              onSubmitWithCode={(c) => onVerify(c)}
              onResend={onRequestOtp}
              onBack={() => {
                setStep(STEP_EMAIL);
                setError("");
                setInfo("");
              }}
              busy={busy}
              error={error}
              info={info}
            />
          )}
          {step === STEP_DASHBOARD && (
            <Dashboard email={getPortalEmail(slug) || email} onLogout={onLogout} />
          )}
        </div>
      </div>
    </PortalContext.Provider>
  );
}

function BrandBar() {
  const { restaurant } = usePortal();
  const imageSrc = restaurant?.imageUrl
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${restaurant.imageUrl}`
    : null;
  const initial = (restaurant?.name || "T").trim().charAt(0).toUpperCase();
  return (
    <div className="gp-brand-bar">
      {imageSrc ? (
        <img
          className="gp-brand-photo"
          src={imageSrc}
          alt={restaurant?.name || "Restaurant"}
        />
      ) : (
        <span className="gp-brand-initial" aria-hidden="true">{initial}</span>
      )}
      <span className="gp-brand-name">{restaurant?.name || "Tali Restaurants"}</span>
      <span className="gp-brand-tag">Guest Portal</span>
    </div>
  );
}

function EmailStep({ email, setEmail, onSubmit, busy, error, info }) {
  return (
    <div className="gp-card gp-card-narrow">
      <div className="gp-card-body">
        <p className="gp-eyebrow">Welcome back</p>
        <h2 className="gp-title">Manage your booking</h2>
        <p className="gp-sub">
          Enter the email you used for your reservation or experience booking. We’ll send a one-time code to verify it’s really you.
        </p>
        {error && (
          <div className="gp-alert gp-alert-error" role="alert">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{error}</span>
          </div>
        )}
        {info && !error && (
          <div className="gp-alert gp-alert-success" role="status">
            <i className="bi bi-check-circle-fill"></i>
            <span>{info}</span>
          </div>
        )}
        <form onSubmit={onSubmit}>
          <div className="gp-form-row">
            <label className="gp-label" htmlFor="gp-email">
              Email address
            </label>
            <input
              id="gp-email"
              type="email"
              className="gp-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
            />
          </div>
          <button type="submit" className="gp-btn-primary" disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Sending code…
              </>
            ) : (
              <>
                Send 6-digit code
                <i className="bi bi-arrow-right"></i>
              </>
            )}
          </button>
        </form>
        <p className="gp-sub" style={{ marginTop: 24, marginBottom: 0, fontSize: ".85rem" }}>
          <i className="bi bi-shield-lock me-1" style={{ color: "var(--gp-brand)" }}></i>
          We never share your email. Codes expire after 10 minutes.
        </p>
      </div>
    </div>
  );
}

function OtpStep({ email, otp, setOtp, onSubmit, onSubmitWithCode, onResend, onBack, busy, error, info }) {
  const inputsRef = useRef([]);

  useEffect(() => {
    // Focus first cell on mount
    setTimeout(() => inputsRef.current[0]?.focus(), 50);
  }, []);

  const handleChange = (i, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const arr = (otp + "").padEnd(OTP_LENGTH, " ").split("");
    arr[i] = digit || " ";
    const next = arr.join("").replace(/\s+$/, "").replace(/\s/g, "");
    setOtp(next);
    if (digit && i < OTP_LENGTH - 1) {
      inputsRef.current[i + 1]?.focus();
    }
    if (next.length === OTP_LENGTH) {
      // Auto-submit on full code
      setTimeout(() => onSubmitWithCode(next), 60);
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (!otp[i] && i > 0) {
        inputsRef.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < OTP_LENGTH - 1) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!txt) return;
    e.preventDefault();
    setOtp(txt);
    setTimeout(() => {
      const target = Math.min(txt.length, OTP_LENGTH - 1);
      inputsRef.current[target]?.focus();
      if (txt.length === OTP_LENGTH) onSubmitWithCode(txt);
    }, 30);
  };

  const cells = Array.from({ length: OTP_LENGTH }, (_, i) => otp[i] || "");

  return (
    <div className="gp-card gp-card-narrow">
      <div className="gp-card-body">
        <p className="gp-eyebrow">Almost there</p>
        <h2 className="gp-title">Enter verification code</h2>
        <p className="gp-sub">
          We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
        </p>

        {error && (
          <div className="gp-alert gp-alert-error" role="alert">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{error}</span>
          </div>
        )}
        {info && !error && (
          <div className="gp-alert gp-alert-success" role="status">
            <i className="bi bi-check-circle-fill"></i>
            <span>{info}</span>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <div className="gp-form-row" style={{ marginBottom: 8 }}>
            <label className="gp-label" htmlFor="gp-otp-0" style={{ textAlign: "center" }}>
              6-digit code
            </label>
            <div className="gp-otp-row" onPaste={handlePaste}>
              {cells.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  id={`gp-otp-${i}`}
                  className={`gp-otp-cell${digit ? " is-filled" : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>
          </div>
          <button type="submit" className="gp-btn-primary" disabled={busy || otp.length < OTP_LENGTH} style={{ width: "100%", justifyContent: "center" }}>
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Verifying…
              </>
            ) : (
              <>
                Verify and continue
                <i className="bi bi-arrow-right"></i>
              </>
            )}
          </button>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
            <button type="button" className="gp-btn-text" onClick={onBack} disabled={busy}>
              <i className="bi bi-arrow-left me-1"></i>
              Change email
            </button>
            <button type="button" className="gp-btn-text" onClick={onResend} disabled={busy}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Resend code
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const TAB_UPCOMING_RES = "upcoming_reservations";
const TAB_UPCOMING_EVENTS = "upcoming_events";
const TAB_REQUESTS = "requests";
const TAB_HISTORY = "history";

function Dashboard({ email, onLogout }) {
  const { slug } = usePortal();
  const [tab, setTab] = useState(TAB_UPCOMING_RES);
  const tabsRef = useRef(null);
  const [reservations, setReservations] = useState([]);
  const [eventOrders, setEventOrders] = useState([]);
  const [history, setHistory] = useState({ reservations: [], eventOrders: [] });
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requestTarget, setRequestTarget] = useState(null); // { type, item, action }

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const [r, e, h, m] = await Promise.all([
        fetchUpcomingReservations(slug),
        fetchUpcomingEventOrders(slug),
        fetchHistory(slug),
        fetchMyRequests(slug),
      ]);
      setReservations(Array.isArray(r) ? r : []);
      setEventOrders(Array.isArray(e) ? e : []);
      setHistory(h || { reservations: [], eventOrders: [] });
      setMyRequests(Array.isArray(m) ? m : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        onLogout();
        return;
      }
      setError(err?.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(
    () => ({
      [TAB_UPCOMING_RES]: reservations.length,
      [TAB_UPCOMING_EVENTS]: eventOrders.length,
      [TAB_REQUESTS]: myRequests.length,
      [TAB_HISTORY]: (history.reservations?.length || 0) + (history.eventOrders?.length || 0),
    }),
    [reservations, eventOrders, myRequests, history],
  );

  // Count pending requests so the tab badge highlights items needing attention
  const pendingRequestsCount = useMemo(
    () => myRequests.filter((r) => r.status === "pending").length,
    [myRequests],
  );

  // Center the active tab in the scrollable tab strip so the previous/next
  // tabs peek in on the sides — gives users a hint that more tabs exist.
  useEffect(() => {
    const strip = tabsRef.current;
    if (!strip) return;
    const active = strip.querySelector(".gp-tab.active");
    if (!active) return;
    const stripRect = strip.getBoundingClientRect();
    const btnRect = active.getBoundingClientRect();
    const delta = (btnRect.left + btnRect.width / 2) - (stripRect.left + stripRect.width / 2);
    if (Math.abs(delta) > 1) {
      strip.scrollBy({ left: delta, behavior: "smooth" });
    }
  }, [tab]);

  return (
    <div className="gp-card">
      <div className="gp-dash-head">
        <div>
          <h2 className="gp-dash-head-title">Your bookings</h2>
          <div className="gp-dash-head-sub">
            Signed in as <strong>{email}</strong>
          </div>
        </div>
        <button type="button" className="gp-btn-secondary" onClick={onLogout}>
          <i className="bi bi-box-arrow-right"></i>
          Sign out
        </button>
      </div>

      <div className="gp-tabs" role="tablist" ref={tabsRef}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === TAB_UPCOMING_RES}
          className={`gp-tab${tab === TAB_UPCOMING_RES ? " active" : ""}`}
          onClick={() => setTab(TAB_UPCOMING_RES)}
        >
          <i className="bi bi-calendar-check"></i>
          Reservations
          <span className="gp-tab-count">{counts[TAB_UPCOMING_RES]}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === TAB_UPCOMING_EVENTS}
          className={`gp-tab${tab === TAB_UPCOMING_EVENTS ? " active" : ""}`}
          onClick={() => setTab(TAB_UPCOMING_EVENTS)}
        >
          <i className="bi bi-ticket-perforated"></i>
          {/* Renamed from "Show bookings" per client request (2026-06-02) — original kept here for restore. */}
          Experience
          <span className="gp-tab-count">{counts[TAB_UPCOMING_EVENTS]}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === TAB_REQUESTS}
          className={`gp-tab${tab === TAB_REQUESTS ? " active" : ""}`}
          onClick={() => setTab(TAB_REQUESTS)}
        >
          <i className="bi bi-envelope-paper"></i>
          My requests
          <span className="gp-tab-count">
            {counts[TAB_REQUESTS]}
            {pendingRequestsCount > 0 && tab !== TAB_REQUESTS ? (
              <span
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#dc3545", marginLeft: 5, display: "inline-block",
                }}
                aria-label={`${pendingRequestsCount} pending`}
              ></span>
            ) : null}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === TAB_HISTORY}
          className={`gp-tab${tab === TAB_HISTORY ? " active" : ""}`}
          onClick={() => setTab(TAB_HISTORY)}
        >
          <i className="bi bi-clock-history"></i>
          History
          <span className="gp-tab-count">{counts[TAB_HISTORY]}</span>
        </button>
      </div>

      {error && (
        <div style={{ padding: "16px 28px 0" }}>
          <div className="gp-alert gp-alert-error" role="alert">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{error}</span>
          </div>
        </div>
      )}

      {loading && (
        <div className="gp-list">
          {[1, 2].map((i) => <div key={i} className="gp-skel gp-skel-row" />)}
        </div>
      )}

      {!loading && tab === TAB_UPCOMING_RES && (
        <ReservationList
          items={reservations}
          onAction={(item, action) =>
            setRequestTarget({ type: "reservation", item, action })
          }
          allowActions
        />
      )}

      {!loading && tab === TAB_UPCOMING_EVENTS && (
        <EventOrderList
          items={eventOrders}
          onAction={(item, action) =>
            setRequestTarget({ type: "event_order", item, action })
          }
          allowActions
        />
      )}

      {!loading && tab === TAB_REQUESTS && (
        <MyRequestsList items={myRequests} onCancelled={reload} />
      )}

      {!loading && tab === TAB_HISTORY && (
        <>
          <h4 className="gp-section-heading">Past reservations</h4>
          <ReservationList items={history.reservations} />
          <h4 className="gp-section-heading">Past experience bookings</h4>
          <EventOrderList items={history.eventOrders} />
        </>
      )}

      {requestTarget && (
        <RequestModal
          target={requestTarget}
          onClose={() => setRequestTarget(null)}
          onSubmitted={() => {
            setRequestTarget(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ---- Helpers ----
const reservationStatusBadge = (status) => {
  switch (status) {
    case "confirmed": return { cls: "gp-badge-success", label: "Confirmed" };
    case "awaiting_approval": return { cls: "gp-badge-warn", label: "Awaiting approval" };
    case "pending": return { cls: "gp-badge-warn", label: "Pending" };
    case "waitlist": return { cls: "gp-badge-muted", label: "Waitlist" };
    case "cancelled": return { cls: "gp-badge-danger", label: "Cancelled" };
    default: return { cls: "gp-badge-info", label: status || "—" };
  }
};

const eventOrderStatusBadge = (status) => {
  switch (status) {
    case "paid":
    case "paid_cash":
    case "bypassed": return { cls: "gp-badge-success", label: "Paid" };
    case "approved": return { cls: "gp-badge-warn", label: "Awaiting payment" };
    case "partially_paid": return { cls: "gp-badge-warn", label: "Partially paid" };
    case "pending": return { cls: "gp-badge-warn", label: "Pending" };
    case "cancelled": return { cls: "gp-badge-danger", label: "Cancelled" };
    case "refunded": return { cls: "gp-badge-muted", label: "Refunded" };
    default: return { cls: "gp-badge-info", label: status || "—" };
  }
};

function ReservationList({ items, onAction, allowActions }) {
  if (!items || items.length === 0) {
    return (
      <div className="gp-empty">
        <span className="gp-empty-icon"><i className="bi bi-calendar2-x"></i></span>
        <strong>No upcoming reservations</strong>
        <div style={{ marginTop: 4 }}>When you make a booking, it’ll show up here.</div>
      </div>
    );
  }
  return (
    <div className="gp-list">
      {items.map((r) => {
        const badge = reservationStatusBadge(r.status);
        return (
          <div className="gp-item" key={r.id}>
            <span className="gp-item-icon"><i className="bi bi-calendar-check"></i></span>
            <div className="gp-item-body">
              <h3 className="gp-item-title">
                {r.restaurant?.name || "Reservation"}
                <span style={{ fontWeight: 500, color: "var(--gp-text-muted)", fontSize: ".82rem" }}>
                  {" "}· Reservation #{r.id}
                </span>
                {(r.firstName || r.lastName) && (
                  <span style={{ fontWeight: 500, color: "var(--gp-text-muted)", fontSize: ".82rem" }}>
                    {" "}· {`${r.firstName || ""} ${r.lastName || ""}`.trim()}
                  </span>
                )}
              </h3>
              <div className="gp-item-meta">
                <span><i className="bi bi-calendar3"></i>{fmtDate(r.date)}</span>
                {r.time && <span><i className="bi bi-clock"></i>{fmtTime12h(r.time)}</span>}
                <span><i className="bi bi-people"></i>{r.guests} {r.guests === 1 ? "guest" : "guests"}</span>
                {r.specialOccasion && (
                  <span><i className="bi bi-balloon-heart"></i>{r.specialOccasion}</span>
                )}
              </div>
              {allowActions && (
                r.requestsClosed ? (
                  <div
                    className="gp-item-notice"
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "var(--gp-warning-bg)",
                      border: "1px solid #ffe69c",
                      color: "var(--gp-warning-ink)",
                      fontSize: ".88rem",
                    }}
                  >
                    <i className="bi bi-lock-fill me-1"></i>
                    The change-request window for this reservation has closed.{" "}
                    Please contact the venue directly for any changes.
                  </div>
                ) : (
                  <div className="gp-actions">
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(r, "add_guests")}>
                      <i className="bi bi-plus-circle"></i>
                      Add guests
                    </button>
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(r, "reduce_guests")}>
                      <i className="bi bi-dash-circle"></i>
                      Reduce guests
                    </button>
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(r, "reschedule")}>
                      <i className="bi bi-calendar-event"></i>
                      Change date
                    </button>
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(r, "other")}>
                      <i className="bi bi-chat-dots"></i>
                      Other request
                    </button>
                    <button type="button" className="gp-btn-secondary gp-btn-danger" onClick={() => onAction(r, "cancel")}>
                      <i className="bi bi-x-circle"></i>
                      Cancel
                    </button>
                  </div>
                )
              )}
            </div>
            <div className="gp-item-status">
              <span className={`gp-badge ${badge.cls}`}>{badge.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventOrderList({ items, onAction, allowActions }) {
  if (!items || items.length === 0) {
    return (
      <div className="gp-empty">
        <span className="gp-empty-icon"><i className="bi bi-ticket-perforated"></i></span>
        <strong>No experience bookings</strong>
        <div style={{ marginTop: 4 }}>Bookings you purchase will appear here.</div>
      </div>
    );
  }
  return (
    <div className="gp-list">
      {items.map((o) => {
        const badge = eventOrderStatusBadge(o.status);
        const ticketCount = o.items?.length ?? 0;
        return (
          <div className="gp-item" key={o.id}>
            <span className="gp-item-icon"><i className="bi bi-ticket-perforated"></i></span>
            <div className="gp-item-body">
              <h3 className="gp-item-title">
                {o.event?.name || "Experience"} <span style={{ fontWeight: 500, color: "var(--gp-text-muted)" }}>· Booking #{o.id}</span>
              </h3>
              <div className="gp-item-meta">
                {o.event?.eventDate && <span><i className="bi bi-calendar3"></i>{fmtDate(o.event.eventDate)}</span>}
                {o.event?.startTime && <span><i className="bi bi-clock"></i>{fmtTime12h(o.event.startTime)}</span>}
                <span><i className="bi bi-ticket"></i>{ticketCount} {ticketCount === 1 ? "booking" : "bookings"}</span>
                {o.totalCents != null && <span><i className="bi bi-cash-coin"></i>EGP {(o.totalCents / 100).toFixed(2)}</span>}
              </div>
              {allowActions && (
                o.requestsClosed ? (
                  <div
                    className="gp-item-notice"
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "var(--gp-warning-bg)",
                      border: "1px solid #ffe69c",
                      color: "var(--gp-warning-ink)",
                      fontSize: ".88rem",
                    }}
                  >
                    <i className="bi bi-lock-fill me-1"></i>
                    The change-request window for this experience has closed.{" "}
                    Please contact the venue directly for any changes.
                  </div>
                ) : (
                  <div className="gp-actions">
                    {/* "Add bookings" renamed to "Add guests" per client request (2026-06-02). */}
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(o, "add_guests")}>
                      <i className="bi bi-plus-circle"></i>
                      Add guests
                    </button>
                    {/* Hidden per client request (2026-06-02) — Reduce bookings / Edit data / Change show.
                        Do NOT delete; client may ask to restore. Un-comment this block to bring them back.
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(o, "reduce_guests")}>
                      <i className="bi bi-dash-circle"></i>
                      Reduce bookings
                    </button>
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(o, "edit")}>
                      <i className="bi bi-pencil-square"></i>
                      Edit data
                    </button>
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(o, "transfer")}>
                      <i className="bi bi-arrow-left-right"></i>
                      Change show
                    </button>
                    */}
                    <button type="button" className="gp-btn-secondary" onClick={() => onAction(o, "other")}>
                      <i className="bi bi-chat-dots"></i>
                      Other request
                    </button>
                    <button type="button" className="gp-btn-secondary gp-btn-danger" onClick={() => onAction(o, "cancel")}>
                      <i className="bi bi-x-circle"></i>
                      Cancel booking
                    </button>
                  </div>
                )
              )}
            </div>
            <div className="gp-item-status">
              <span className={`gp-badge ${badge.cls}`}>{badge.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const requestStatusBadge = (status, requestType) => {
  switch (status) {
    case "pending": return { cls: "gp-badge-warn", label: "Pending review", icon: "bi-hourglass-split" };
    case "approved": return { cls: "gp-badge-success", label: "Approved", icon: "bi-check-circle-fill" };
    case "auto_approved": return { cls: "gp-badge-success", label: "Approved", icon: "bi-check-circle-fill" };
    case "auto_approved_refund_failed":
      // Label depends on what was being attempted — cancel/reduce involve a refund,
      // add/edit don't.
      return requestType === "cancel" || requestType === "reduce_guests"
        ? { cls: "gp-badge-warn", label: "Approved · refund pending", icon: "bi-exclamation-triangle-fill" }
        : { cls: "gp-badge-warn", label: "Approved · needs follow-up", icon: "bi-exclamation-triangle-fill" };
    case "rejected": return { cls: "gp-badge-danger", label: "Rejected", icon: "bi-x-circle-fill" };
    case "viewed": return { cls: "gp-badge-info", label: "Seen by venue", icon: "bi-eye-fill" };
    case "responded": return { cls: "gp-badge-success", label: "Replied", icon: "bi-chat-dots-fill" };
    default: return { cls: "gp-badge-muted", label: status || "—", icon: "bi-circle" };
  }
};

const requestTypeLabel = (t) => {
  switch (t) {
    case "cancel": return "Cancel booking";
    case "add_guests": return "Add guests / bookings";
    case "reduce_guests": return "Reduce bookings";
    case "edit": return "Edit booking";
    case "transfer": return "Move to another experience";
    case "reschedule": return "Change date";
    case "other": return "Message to the venue";
    default: return t;
  }
};

const requestTypeIcon = (t) => {
  switch (t) {
    case "cancel": return "bi-x-circle";
    case "add_guests": return "bi-plus-circle";
    case "reduce_guests": return "bi-dash-circle";
    case "edit": return "bi-pencil-square";
    case "transfer": return "bi-arrow-left-right";
    case "reschedule": return "bi-calendar-event";
    case "other": return "bi-chat-dots";
    default: return "bi-envelope-paper";
  }
};

function MyRequestsList({ items, onCancelled }) {
  const { slug } = usePortal();
  const [confirmingId, setConfirmingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [viewingId, setViewingId] = useState(null);

  const requestToView = (items || []).find((r) => r.id === viewingId);

  const requestToCancel = (items || []).find((r) => r.id === confirmingId);

  const doCancel = async () => {
    const id = confirmingId;
    if (!id) return;
    setCancellingId(id);
    setCancelError("");
    try {
      await cancelMyRequest(slug, id);
      setConfirmingId(null);
      if (onCancelled) onCancelled();
    } catch (err) {
      setCancelError(err?.response?.data?.message || err.message || "Failed to cancel request");
    } finally {
      setCancellingId(null);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="gp-empty">
        <span className="gp-empty-icon"><i className="bi bi-envelope-paper"></i></span>
        <strong>No requests yet</strong>
        <div style={{ marginTop: 4 }}>
          When you ask to cancel, edit, or change a booking, the request shows up here so you can track its status.
        </div>
      </div>
    );
  }
  return (
    <div className="gp-list">
      {cancelError && (
        <div className="gp-alert gp-alert-error" role="alert">
          <i className="bi bi-exclamation-circle-fill"></i>
          <span>{cancelError}</span>
        </div>
      )}
      {items.map((r) => {
        const badge = requestStatusBadge(r.status, r.requestType);
        const target = r.targetType === "event_order"
          ? `Experience Booking #${r.targetId}`
          : `Reservation #${r.targetId}`;
        const changes = r.requestedChanges || {};
        return (
          <div className="gp-item" key={r.id}>
            <span className="gp-item-icon"><i className={`bi ${requestTypeIcon(r.requestType)}`}></i></span>
            <div className="gp-item-body">
              <h3 className="gp-item-title">
                {requestTypeLabel(r.requestType)}{" "}
                <span style={{ fontWeight: 500, color: "var(--gp-text-muted)" }}>· {target}</span>
              </h3>
              <div className="gp-item-meta">
                <span><i className="bi bi-hash"></i>Request #{r.id}</span>
                <span><i className="bi bi-calendar3"></i>{fmtDateTime(r.createdAt)}</span>
                {Number.isFinite(Number(changes.count)) && (
                  <span>
                    <i className="bi bi-people"></i>
                    {r.requestType === "add_guests" ? "+" : r.requestType === "reduce_guests" ? "-" : ""}
                    {changes.count}
                  </span>
                )}
                {Array.isArray(changes.itemIds) && changes.itemIds.length > 0 && (
                  <span>
                    <i className="bi bi-ticket"></i>
                    {changes.itemIds.length} booking{changes.itemIds.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              {changes.notes && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    background: "#fafafa",
                    border: "1px solid var(--gp-border)",
                    borderRadius: 8,
                    fontSize: ".85rem",
                    color: "var(--gp-text-muted)",
                  }}
                >
                  <strong style={{ color: "var(--gp-text)" }}>Your note:</strong> {changes.notes}
                </div>
              )}
              {r.status === "rejected" && r.rejectReason && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    background: "var(--gp-danger-bg)",
                    border: "1px solid #f4c2c2",
                    borderRadius: 8,
                    fontSize: ".88rem",
                    color: "#7a1421",
                  }}
                >
                  <strong>
                    <i className="bi bi-chat-left-text me-1"></i>
                    Message from the venue:
                  </strong>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{r.rejectReason}</div>
                </div>
              )}
              {r.adminReply && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    background: "#eef6ff",
                    border: "1px solid #cfe2ff",
                    borderRadius: 8,
                    fontSize: ".88rem",
                    color: "#0a3d62",
                  }}
                >
                  <strong>
                    <i className="bi bi-chat-dots me-1"></i>
                    Reply from the venue:
                  </strong>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{r.adminReply}</div>
                </div>
              )}
              {r.status === "auto_approved_refund_failed" && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    background: "var(--gp-warning-bg)",
                    border: "1px solid #ffe69c",
                    borderRadius: 8,
                    fontSize: ".88rem",
                    color: "var(--gp-warning-ink)",
                  }}
                >
                  <i className="bi bi-info-circle-fill me-1"></i>
                  {r.requestType === "cancel" || r.requestType === "reduce_guests"
                    ? "Your request was approved but the automatic refund needs manual processing. The venue will contact you shortly."
                    : "Your request was approved but couldn’t be applied automatically. The venue will follow up to finish it."}
                </div>
              )}
              {r.processedAt && (r.status === "approved" || r.status === "auto_approved" || r.status === "rejected") && (
                <div style={{ marginTop: 6, fontSize: ".78rem", color: "var(--gp-text-muted)" }}>
                  Resolved {fmtDateTime(r.processedAt)}
                </div>
              )}
              <div className="gp-actions">
                <button
                  type="button"
                  className="gp-btn-secondary"
                  onClick={() => setViewingId(r.id)}
                >
                  <i className="bi bi-eye"></i>
                  View details
                </button>
                {r.status === "pending" && (
                  <button
                    type="button"
                    className="gp-btn-secondary gp-btn-danger"
                    onClick={() => setConfirmingId(r.id)}
                  >
                    <i className="bi bi-x-circle"></i>
                    Cancel request
                  </button>
                )}
              </div>
            </div>
            <div className="gp-item-status">
              <span className={`gp-badge ${badge.cls}`}>
                <i className={`bi ${badge.icon}`}></i>
                {badge.label}
              </span>
            </div>
          </div>
        );
      })}

      {confirmingId !== null && (
        <ConfirmDialog
          title="Cancel this request?"
          message={
            requestToCancel
              ? `Your ${requestTypeLabel(requestToCancel.requestType).toLowerCase()} request will be withdrawn. The venue won't review it any further.`
              : "The venue won't review it any further."
          }
          confirmLabel="Cancel request"
          cancelLabel="Keep it"
          busy={cancellingId === confirmingId}
          onConfirm={doCancel}
          onClose={() => {
            if (cancellingId !== null) return;
            setConfirmingId(null);
            setCancelError("");
          }}
        />
      )}
      {requestToView && (
        <RequestDetailsModal
          request={requestToView}
          onClose={() => setViewingId(null)}
        />
      )}
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", busy, onConfirm, onClose }) {
  return (
    <div className="gp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="gp-modal"
        style={{ maxWidth: 440 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gp-modal-head" style={{ alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "var(--gp-danger-bg)",
                color: "var(--gp-danger)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              <i className="bi bi-exclamation-triangle-fill"></i>
            </span>
            <h3 style={{ margin: 0 }}>{title}</h3>
          </div>
          <button
            type="button"
            className="gp-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="gp-modal-body">
          <p style={{ margin: 0, color: "var(--gp-text-muted)" }}>{message}</p>
        </div>
        <div className="gp-modal-foot">
          <button type="button" className="gp-btn-secondary" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="gp-btn-primary"
            onClick={onConfirm}
            disabled={busy}
            style={{
              background: "linear-gradient(135deg, #b00020 0%, #d12d40 100%)",
              color: "#fff",
            }}
          >
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Working…
              </>
            ) : (
              <>
                <i className="bi bi-x-circle"></i>
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetailsModal({ request, onClose }) {
  const r = request;
  const badge = requestStatusBadge(r.status, r.requestType);
  const target = r.targetType === "event_order"
    ? `Experience Booking #${r.targetId}`
    : `Reservation #${r.targetId}`;
  const changes = r.requestedChanges || {};
  const snap = r.target || {};

  // Natural-language summary at the top of the modal so the guest reads what
  // they actually asked for ("Change from X to Y", "Add 2 guests", etc.).
  let summary = "";
  const isEvent = r.targetType === "event_order";
  switch (r.requestType) {
    case "cancel":
      summary = isEvent
        ? `Cancel booking at ${snap.eventName || "the experience"}.`
        : `Cancel reservation${snap.restaurantName ? " at " + snap.restaurantName : ""}.`;
      break;
    case "reschedule": {
      const fromDate = snap.date ? fmtDate(snap.date) : null;
      const fromTime = snap.time ? fmtTime12h(String(snap.time).substring(0, 5)) : null;
      const toDate = changes.date ? fmtDate(changes.date) : null;
      const toTime = changes.time ? fmtTime12h(String(changes.time).substring(0, 5)) : null;
      const fromShift = changes.currentShiftLabel || snap.shiftLabel || null;
      const toShift = changes.shiftLabel || null;
      const shiftBit =
        toShift && fromShift && toShift !== fromShift
          ? ` Shift: ${fromShift} → ${toShift}.`
          : toShift
            ? ` Shift: ${toShift}.`
            : "";
      summary = `Change reservation from ${fromDate || "?"} ${fromTime || ""} to ${toDate || "?"} ${toTime || ""}.${shiftBit}`;
      break;
    }
    case "add_guests": {
      const n = Number(changes.count) || (Array.isArray(changes.tickets) ? changes.tickets.length : 0);
      if (isEvent) {
        const holders = Array.isArray(changes.tickets) ? changes.tickets : [];
        const holderBits = holders.map((t, i) =>
          t.sameAsBuyer ? `#${i + 1} same as buyer` : `#${i + 1} ${[t.firstName, t.lastName].filter(Boolean).join(" ") || "(no name)"}`
        ).join(", ");
        summary = `Add ${n} booking${n === 1 ? "" : "s"}${holderBits ? " — " + holderBits : ""}.`;
      } else {
        summary = `Add ${n} ${n === 1 ? "guest" : "guests"} to your reservation (${snap.guests || "?"} → ${(snap.guests || 0) + n}).`;
      }
      break;
    }
    case "reduce_guests": {
      if (isEvent) {
        const ids = Array.isArray(changes.itemIds) ? changes.itemIds : [];
        summary = `Refund ${ids.length} booking${ids.length === 1 ? "" : "s"} (${ids.map((i) => "#" + i).join(", ")}).`;
      } else {
        const n = Number(changes.count) || 0;
        summary = `Remove ${n} ${n === 1 ? "guest" : "guests"} from your reservation (${snap.guests || "?"} → ${Math.max(1, (snap.guests || 0) - n)}).`;
      }
      break;
    }
    case "edit": {
      const updates = Array.isArray(changes.itemUpdates) ? changes.itemUpdates : [];
      summary = `Update ${updates.length} booking holder${updates.length === 1 ? "" : "s"}.`;
      break;
    }
    case "transfer":
      summary = `Move this booking to experience #${changes.targetEventId}.`;
      break;
    case "other":
      summary = changes.message
        ? `You sent the venue a message: "${changes.message}"`
        : "You sent the venue a message.";
      break;
    default:
      summary = requestTypeLabel(r.requestType);
  }
  const statusReadable = (() => {
    switch (r.status) {
      case "pending": return "Pending review";
      case "approved":
      case "auto_approved": return "Approved";
      case "rejected": return "Rejected";
      case "cancelled_by_guest": return "Cancelled by you";
      case "auto_approved_refund_failed": return "Approved — refund pending";
      case "viewed": return "Seen by the venue";
      case "responded": return "Venue replied";
      default: return r.status;
    }
  })();

  const rows = [];
  if (Number.isFinite(Number(changes.count))) {
    const sign = r.requestType === "add_guests" ? "+" : r.requestType === "reduce_guests" ? "-" : "";
    rows.push({ label: r.targetType === "event_order" ? "Bookings" : "Guests", value: `${sign}${changes.count}` });
  }
  if (Array.isArray(changes.itemIds) && changes.itemIds.length > 0) {
    rows.push({ label: "Selected bookings", value: changes.itemIds.map((id) => `#${id}`).join(", ") });
  }
  if (changes.date) rows.push({ label: "New date", value: fmtDate(changes.date) });
  if (changes.time) rows.push({ label: "New time", value: fmtTime12h(String(changes.time).substring(0, 5)) });
  if (changes.targetEventId) rows.push({ label: "Target experience", value: `#${changes.targetEventId}` });
  if (Array.isArray(changes.tickets) && changes.tickets.length > 0) {
    rows.push({
      label: "New booking holders",
      value: changes.tickets.map((t, i) =>
        t.sameAsBuyer ? `#${i + 1}: same as buyer` : `#${i + 1}: ${[t.firstName, t.lastName].filter(Boolean).join(" ") || "(no name)"}${t.email ? " · " + t.email : ""}`
      ).join("\n"),
    });
  }
  if (Array.isArray(changes.itemUpdates) && changes.itemUpdates.length > 0) {
    rows.push({
      label: "Booking data updates",
      value: changes.itemUpdates.map((u) =>
        `Booking #${u.itemId}: ${[u.firstName, u.lastName].filter(Boolean).join(" ") || ""}${u.email ? " · " + u.email : ""}${u.phone ? " · " + u.phone : ""}`
      ).join("\n"),
    });
  }

  return (
    <div className="gp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="gp-modal"
        style={{ maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gp-modal-head" style={{ alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "var(--gp-accent-bg, #f1f3ff)",
                color: "var(--gp-text)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              <i className={`bi ${requestTypeIcon(r.requestType)}`}></i>
            </span>
            <div>
              <h3 style={{ margin: 0 }}>{requestTypeLabel(r.requestType)}</h3>
              <div style={{ fontSize: ".82rem", color: "var(--gp-text-muted)" }}>
                {target} · Request #{r.id}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="gp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="gp-modal-body">
          {summary && (
            <div
              style={{
                marginBottom: 14,
                padding: "12px 14px",
                background: "var(--gp-accent-bg, #f5f3ff)",
                border: "1px solid var(--gp-border)",
                borderRadius: 10,
                fontSize: ".92rem",
                lineHeight: 1.45,
              }}
            >
              <div style={{ marginBottom: 6, fontSize: ".74rem", textTransform: "uppercase", letterSpacing: ".05em", color: "var(--gp-text-muted)" }}>
                What you asked for
              </div>
              <div style={{ color: "var(--gp-text)" }}>{summary}</div>
              <div style={{ marginTop: 8, fontSize: ".82rem", color: "var(--gp-text-muted)" }}>
                Status: <strong style={{ color: "var(--gp-text)" }}>{statusReadable}</strong>
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: ".82rem", color: "var(--gp-text-muted)" }}>Status</span>
            <span className={`gp-badge ${badge.cls}`}>
              <i className={`bi ${badge.icon}`}></i>
              {badge.label}
            </span>
          </div>
          <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px", fontSize: ".88rem" }}>
            <dt style={{ color: "var(--gp-text-muted)" }}>Submitted</dt>
            <dd style={{ margin: 0 }}>{fmtDateTime(r.createdAt)}</dd>
            {r.processedAt && (
              <>
                <dt style={{ color: "var(--gp-text-muted)" }}>Resolved</dt>
                <dd style={{ margin: 0 }}>{fmtDateTime(r.processedAt)}</dd>
              </>
            )}
            {rows.map((row) => (
              <React.Fragment key={row.label}>
                <dt style={{ color: "var(--gp-text-muted)" }}>{row.label}</dt>
                <dd style={{ margin: 0, whiteSpace: "pre-wrap" }}>{row.value}</dd>
              </React.Fragment>
            ))}
          </dl>
          {changes.notes && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: "#fafafa",
                border: "1px solid var(--gp-border)",
                borderRadius: 8,
                fontSize: ".88rem",
              }}
            >
              <strong>Your note</strong>
              <div style={{ marginTop: 4, whiteSpace: "pre-wrap", color: "var(--gp-text-muted)" }}>{changes.notes}</div>
            </div>
          )}
          {r.status === "rejected" && r.rejectReason && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                background: "var(--gp-danger-bg)",
                border: "1px solid #f4c2c2",
                borderRadius: 8,
                fontSize: ".88rem",
                color: "#7a1421",
              }}
            >
              <strong>
                <i className="bi bi-chat-left-text me-1"></i>
                Message from the venue
              </strong>
              <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{r.rejectReason}</div>
            </div>
          )}
          {r.adminReply && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                background: "#eef6ff",
                border: "1px solid #cfe2ff",
                borderRadius: 8,
                fontSize: ".88rem",
                color: "#0a3d62",
              }}
            >
              <strong>
                <i className="bi bi-chat-dots me-1"></i>
                Reply from the venue
              </strong>
              <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{r.adminReply}</div>
            </div>
          )}
          {r.status === "auto_approved_refund_failed" && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                background: "var(--gp-warning-bg)",
                border: "1px solid #ffe69c",
                borderRadius: 8,
                fontSize: ".88rem",
                color: "var(--gp-warning-ink)",
              }}
            >
              <i className="bi bi-info-circle-fill me-1"></i>
              {r.requestType === "cancel" || r.requestType === "reduce_guests"
                ? "Your request was approved but the automatic refund needs manual processing. The venue will contact you shortly."
                : "Your request was approved but couldn’t be applied automatically. The venue will follow up to finish it."}
            </div>
          )}
        </div>
        <div className="gp-modal-foot">
          <button type="button" className="gp-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDateTime(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

function RequestModal({ target, onClose, onSubmitted }) {
  const { slug } = usePortal();
  const [count, setCount] = useState(1);
  const [notes, setNotes] = useState("");
  const [otherMessage, setOtherMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  const action = target.action;
  const isOther = action === "other";
  const isEventReduce = action === "reduce_guests" && target.type === "event_order";
  const isEventAdd = action === "add_guests" && target.type === "event_order";
  const isEventEdit = action === "edit" && target.type === "event_order";
  const isEventTransfer = action === "transfer" && target.type === "event_order";
  const isReservationReschedule = action === "reschedule" && target.type === "reservation";
  // Respect the per-experience "Same as buyer" toggle (admin). When the
  // experience has it disabled, the portal must not offer the shortcut either —
  // mirrors the booking widget (ReservationForm.jsx).
  const allowSameAsBuyer = target.item?.event?.allowSameAsBuyer !== false;
  // Shift "Request All Guests Data": when the reservation's shift has it on, the
  // portal collects full per-guest details for the guests being ADDED (name /
  // email / phone, + a social link when the shift also requires social media) —
  // mirroring the booking widget — instead of just asking "how many to add".
  const reservationShift = target.type === "reservation" ? (target.item?.shift || null) : null;
  const collectGuestData =
    action === "add_guests" && target.type === "reservation" && !!reservationShift?.requestAllGuestsData;
  const collectGuestSocial = collectGuestData && !!reservationShift?.requireSocialMedia;
  const guestSocialRequired = collectGuestSocial && !!reservationShift?.socialMediaRequired;
  const isCountInput =
    (action === "add_guests" || action === "reduce_guests") &&
    !isEventReduce && !isEventAdd && !collectGuestData;

  // Reschedule flow: date pill scroller + time pill scroller.
  // Use LOCAL date — toISOString() returns UTC which can land on yesterday in EG/UTC+2.
  const localIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const todayISO = useMemo(() => localIso(new Date()), []);
  // Generate the next 30 days as date pills, starting today (local time).
  const dateOptions = useMemo(() => {
    if (!isReservationReschedule) return [];
    const out = [];
    const base = new Date(todayISO + "T00:00:00");
    for (let i = 0; i < 30; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push({
        iso: localIso(d),
        weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
        day: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      });
    }
    return out;
  }, [isReservationReschedule, todayISO]);
  // Current reservation date/time → use as the initial selection so the modal
  // opens "showing where the guest is now". Past dates fall back to today.
  const currentReservationDateIso = useMemo(() => {
    if (target.type !== "reservation") return null;
    const raw = target.item?.date;
    if (!raw) return null;
    const d = typeof raw === "string" ? new Date(raw) : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return localIso(d);
  }, [target.item?.date, target.type]);
  const currentReservationTime = useMemo(() => {
    if (target.type !== "reservation") return null;
    const t = target.item?.time;
    if (!t) return null;
    return String(t).length >= 5 ? String(t).substring(0, 5) : String(t);
  }, [target.item?.time, target.type]);
  const initialRescheduleDate =
    currentReservationDateIso && currentReservationDateIso >= todayISO
      ? currentReservationDateIso
      : todayISO;
  const [rescheduleDate, setRescheduleDate] = useState(initialRescheduleDate);
  const [rescheduleSlot, setRescheduleSlot] = useState(null);
  const [rescheduleShiftId, setRescheduleShiftId] = useState(null);
  const [slotsData, setSlotsData] = useState(null); // null = idle, object = loaded, .error string on failure
  const [dateMode, setDateMode] = useState("quick"); // "quick" | "calendar"
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const d = new Date(todayISO + "T00:00:00");
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const dateScrollRef = useRef(null);
  const timeScrollRef = useRef(null);
  const scrollByDir = (ref, direction) => {
    if (!ref?.current) return;
    ref.current.scrollBy({ left: direction * 220, behavior: "smooth" });
  };
  useEffect(() => {
    if (!isReservationReschedule) return;
    let cancelled = false;
    setSlotsData(null);
    setRescheduleSlot(null);
    setRescheduleShiftId(null);
    fetchRescheduleSlots(slug, target.item.id, rescheduleDate)
      .then((data) => {
        if (cancelled) return;
        setSlotsData(data);
        // If this is the reservation's current date, try to preselect its current time.
        if (
          rescheduleDate === currentReservationDateIso &&
          currentReservationTime &&
          Array.isArray(data?.shifts)
        ) {
          for (const sh of data.shifts) {
            const found = (sh.slots || []).find((s) => s.time === currentReservationTime);
            if (found && !found.isBlocked && !found.isPast && (!found.isFull || sh.allowsWaitlist)) {
              setRescheduleSlot(found.time);
              setRescheduleShiftId(sh.id);
              break;
            }
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setSlotsData({ slots: [], eligible: false, error: err?.response?.data?.message || err.message || "Failed to load slots" });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReservationReschedule, target.item.id, rescheduleDate, currentReservationDateIso, currentReservationTime]);
  // Auto-scroll the date strip so the selected pill is visible.
  useEffect(() => {
    if (!isReservationReschedule || !dateScrollRef.current) return;
    const el = dateScrollRef.current.querySelector(`[data-date="${rescheduleDate}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [isReservationReschedule, rescheduleDate]);

  // Buyer fields used by the "Same as buyer" shortcut on add-ticket forms.
  const buyer = useMemo(
    () => ({
      firstName: target.item?.buyerFirstName || "",
      lastName: target.item?.buyerLastName || "",
      email: target.item?.buyerEmail || "",
      phone: target.item?.buyerPhone || "",
    }),
    [target.item],
  );

  // Available ticket types from the event (joined by API). Filter to active ones if flagged.
  const availableTicketTypes = useMemo(() => {
    const fromEvent = target.item?.event?.ticketTypes;
    if (Array.isArray(fromEvent) && fromEvent.length > 0) {
      return fromEvent.filter((tt) => tt && tt.id && (tt.isActive !== false));
    }
    // Fallback: derive from existing order items so the picker always has options.
    const seen = new Map();
    (target.item?.items || []).forEach((it) => {
      if (it?.ticketType?.id && !seen.has(it.ticketType.id)) seen.set(it.ticketType.id, it.ticketType);
    });
    return Array.from(seen.values());
  }, [target.item]);

  const defaultTicketTypeId = useMemo(() => {
    const baseItem = (target.item?.items || []).find((i) => i.ticketTypeId);
    return baseItem?.ticketTypeId || availableTicketTypes[0]?.id || "";
  }, [target.item, availableTicketTypes]);

  const blankHolder = () => ({
    sameAsBuyer: false,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    ticketTypeId: defaultTicketTypeId,
  });
  const [tickets, setTickets] = useState(() => (isEventAdd ? [blankHolder()] : []));

  // Per-guest data rows for the "Add guests" flow when the shift requests all
  // guests' data. One row per guest being added. socialMediaLink is only used
  // when the shift requires social media.
  const blankResGuest = () => ({ firstName: "", lastName: "", email: "", phone: "", socialMediaLink: "" });
  const [resGuests, setResGuests] = useState(() => (collectGuestData ? [blankResGuest()] : []));
  const updateResGuest = (idx, patch) =>
    setResGuests((prev) => prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  const addResGuestRow = () => setResGuests((prev) => [...prev, blankResGuest()]);
  const removeResGuestRow = (idx) =>
    setResGuests((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // For "Edit data" — clone existing tickets so the guest can edit them in-place.
  const editableTickets = useMemo(
    () =>
      isEventEdit
        ? (target.item.items || [])
            .filter((i) => i.status !== "cancelled" && i.status !== "refunded")
            .map((it) => ({
              itemId: it.id,
              firstName: it.firstName || "",
              lastName: it.lastName || "",
              email: it.email || "",
              phone: it.phone || "",
              ticketTypeName: it.ticketType?.name || "",
              original: {
                firstName: it.firstName || "",
                lastName: it.lastName || "",
                email: it.email || "",
                phone: it.phone || "",
              },
            }))
        : [],
    [isEventEdit, target.item],
  );
  const [editTickets, setEditTickets] = useState(editableTickets);
  useEffect(() => {
    setEditTickets(editableTickets);
  }, [editableTickets]);
  const updateEditTicket = (idx, patch) => {
    setEditTickets((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  // Transfer flow: load eligible target events when the modal opens.
  const [transferTargets, setTransferTargets] = useState(null); // null = loading
  const [selectedTargetEventId, setSelectedTargetEventId] = useState(null);
  useEffect(() => {
    if (!isEventTransfer) return;
    let cancelled = false;
    setTransferTargets(null);
    fetchTransferTargets(slug, target.item.id)
      .then((data) => { if (!cancelled) setTransferTargets(data); })
      .catch((err) => {
        if (!cancelled) setTransferTargets({ candidates: [], error: err?.response?.data?.message || err.message || "Failed to load events" });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventTransfer, target.item.id]);

  const ticketRefs = useRef([]);
  const modalBodyRef = useRef(null);
  // Scroll the modal body to top and flash a focus pulse whenever we set an error,
  // so a guest who's scrolled to the bottom of a long form actually sees it.
  const flashError = (msg) => {
    setError(msg);
    requestAnimationFrame(() => {
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  };
  const prevTicketsLen = useRef(tickets.length);
  useEffect(() => {
    if (tickets.length > prevTicketsLen.current) {
      const last = ticketRefs.current[tickets.length - 1];
      if (last && typeof last.scrollIntoView === "function") {
        last.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    prevTicketsLen.current = tickets.length;
  }, [tickets.length]);
  const prevResGuestsLen = useRef(resGuests.length);
  useEffect(() => {
    if (resGuests.length > prevResGuestsLen.current) {
      const last = ticketRefs.current[resGuests.length - 1];
      if (last && typeof last.scrollIntoView === "function") {
        last.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    prevResGuestsLen.current = resGuests.length;
  }, [resGuests.length]);

  const refundableTickets = isEventReduce
    ? (target.item.items || []).filter((i) => i.status !== "cancelled" && i.status !== "refunded")
    : [];

  const toggleTicket = (id) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const updateTicket = (idx, patch) => {
    setTickets((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const toggleSameAsBuyer = (idx) => {
    setTickets((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const next = !t.sameAsBuyer;
        return next
          ? { ...t, sameAsBuyer: true, firstName: buyer.firstName, lastName: buyer.lastName, email: buyer.email, phone: buyer.phone }
          : { ...t, sameAsBuyer: false };
      }),
    );
  };
  const addTicketRow = () => setTickets((prev) => [...prev, blankHolder()]);
  const removeTicketRow = (idx) =>
    setTickets((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Counts / limits used by validation and preview blocks.
  const reservationCurrentGuests = target.type === "reservation" ? (target.item?.guests ?? 0) : 0;
  const orderActiveItems = target.type === "event_order"
    ? (target.item?.items || []).filter((i) => i.status !== "cancelled" && i.status !== "refunded")
    : [];
  const orderCurrentTickets = orderActiveItems.length;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");

    // Reservation reduce: never let the guest reduce to zero — that's a cancel.
    if (action === "reduce_guests" && target.type === "reservation") {
      const n = Number(count) || 0;
      if (n < 1) {
        flashError("Please enter at least 1.");
        return;
      }
      if (n >= reservationCurrentGuests) {
        flashError(
          `You have ${reservationCurrentGuests} ${reservationCurrentGuests === 1 ? "guest" : "guests"}. To remove everyone, please use Cancel instead.`,
        );
        return;
      }
    }

    if (isEventReduce && selectedItemIds.length === 0) {
      flashError("Please select at least one booking to remove.");
      return;
    }

    // Event-order reduce: never let the guest remove all tickets (= cancel) and
    // respect each ticket type's minimum-per-order rule.
    if (isEventReduce) {
      if (selectedItemIds.length >= orderCurrentTickets) {
        flashError(
          `You can't remove all ${orderCurrentTickets} booking${orderCurrentTickets === 1 ? "" : "s"}. To do that, please cancel the booking instead.`,
        );
        return;
      }
      const remainingByType = new Map();
      for (const it of orderActiveItems) {
        if (selectedItemIds.includes(it.id)) continue;
        const tid = it.ticketType?.id ?? it.ticketTypeId;
        remainingByType.set(tid, (remainingByType.get(tid) || 0) + 1);
      }
      const typesSeen = new Set();
      for (const it of orderActiveItems) {
        const tt = it.ticketType;
        if (!tt || typesSeen.has(tt.id)) continue;
        typesSeen.add(tt.id);
        const remaining = remainingByType.get(tt.id) || 0;
        const minPer = Number(tt.minPerOrder) || 0;
        if (remaining > 0 && remaining < minPer) {
          flashError(
            `"${tt.name}" requires at least ${minPer} booking${minPer === 1 ? "" : "s"} per experience. You'd be left with ${remaining}.`,
          );
          return;
        }
      }
    }
    if (isEventAdd) {
      if (tickets.length === 0) {
        flashError("Add at least one booking.");
        return;
      }
      for (const [i, t] of tickets.entries()) {
        if (!t.firstName?.trim() || !t.lastName?.trim()) {
          flashError(`Booking #${i + 1}: please enter first and last name.`);
          return;
        }
        if (!t.email?.trim()) {
          flashError(`Booking #${i + 1}: please enter an email.`);
          return;
        }
        if (!t.phone?.trim() || t.phone.trim().length < 5) {
          flashError(`Booking #${i + 1}: please enter a valid phone number.`);
          return;
        }
      }
    }

    if (collectGuestData) {
      if (resGuests.length === 0) {
        flashError("Please add at least one guest.");
        return;
      }
      for (const [i, g] of resGuests.entries()) {
        if (!g.firstName?.trim() || !g.lastName?.trim()) {
          flashError(`Guest #${i + 1}: please enter first and last name.`);
          return;
        }
        if (!g.email?.trim()) {
          flashError(`Guest #${i + 1}: please enter an email.`);
          return;
        }
        if (guestSocialRequired && !g.socialMediaLink?.trim()) {
          flashError(`Guest #${i + 1}: a social media link is required.`);
          return;
        }
      }
    }

    let itemUpdates = [];
    if (isEventEdit) {
      itemUpdates = editTickets
        .filter((t) =>
          t.firstName !== t.original.firstName ||
          t.lastName !== t.original.lastName ||
          t.email !== t.original.email ||
          t.phone !== t.original.phone,
        )
        .map(({ itemId, firstName, lastName, email, phone }) => ({
          itemId, firstName, lastName, email, phone,
        }));
      if (itemUpdates.length === 0) {
        flashError("Nothing changed. Update at least one booking's details.");
        return;
      }
      for (const upd of itemUpdates) {
        if (!upd.firstName?.trim() || !upd.lastName?.trim()) {
          flashError("First and last name are required for every booking.");
          return;
        }
        if (!upd.email?.trim()) {
          flashError("Email is required for every booking.");
          return;
        }
        if (!upd.phone?.trim() || upd.phone.trim().length < 5) {
          flashError("Please enter a valid phone number for every booking.");
          return;
        }
      }
    }
    if (isEventTransfer) {
      if (!selectedTargetEventId) {
        flashError("Please pick an experience to transfer to.");
        return;
      }
    }

    if (isOther && !otherMessage.trim()) {
      flashError("Please enter a message for the venue.");
      return;
    }

    if (isReservationReschedule) {
      if (!rescheduleDate) {
        flashError("Please pick a date.");
        return;
      }
      if (!rescheduleSlot) {
        flashError("Please pick a time slot.");
        return;
      }
      if (
        rescheduleDate === currentReservationDateIso &&
        rescheduleSlot === currentReservationTime
      ) {
        flashError("That's already your current date and time. Pick a different date or slot to change.");
        return;
      }
    }
    setBusy(true);
    try {
      // Resolve the chosen shift's label (and the reservation's current shift
      // label) so the venue's request view can show the shift change — e.g.
      // "Breakfast → Beach Access" — not just the time, since overlapping shifts
      // can share a time. The backend applies the change by shiftId; the labels
      // are for the admin's reference.
      const rescheduleShift = isReservationReschedule && Array.isArray(slotsData?.shifts)
        ? slotsData.shifts.find((s) => s.id === rescheduleShiftId)
        : null;
      const payload = {
        targetType: target.type === "reservation" ? "reservation" : "event_order",
        targetId: target.item.id,
        requestType: action,
        requestedChanges: {
          ...(isCountInput ? { count: Number(count) } : {}),
          ...(collectGuestData
            ? {
                count: resGuests.length,
                guests: resGuests.map((g) => ({
                  firstName: g.firstName.trim(),
                  lastName: g.lastName.trim(),
                  email: g.email.trim(),
                  ...(g.phone?.trim() ? { phone: g.phone.trim() } : {}),
                  ...(collectGuestSocial && g.socialMediaLink?.trim()
                    ? { socialMediaLink: g.socialMediaLink.trim() }
                    : {}),
                })),
              }
            : {}),
          ...(isEventReduce ? { itemIds: selectedItemIds, count: selectedItemIds.length } : {}),
          ...(isEventAdd
            ? {
                count: tickets.length,
                tickets: tickets.map(({ sameAsBuyer, ...rest }) => ({
                  ...rest,
                  sameAsBuyer: !!sameAsBuyer,
                })),
              }
            : {}),
          ...(isEventEdit ? { itemUpdates } : {}),
          ...(isEventTransfer ? { targetEventId: selectedTargetEventId } : {}),
          ...(isReservationReschedule
            ? {
                date: rescheduleDate,
                time: rescheduleSlot,
                shiftId: rescheduleShiftId,
                ...(rescheduleShift?.label ? { shiftLabel: rescheduleShift.label } : {}),
                ...(target.item?.shiftLabel ? { currentShiftLabel: target.item.shiftLabel } : {}),
              }
            : {}),
          ...(isOther ? { message: otherMessage.trim() } : {}),
          ...(notes ? { notes } : {}),
        },
      };
      await createGuestRequest(slug, payload);
      onSubmitted();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const isTicketPaid = (s) => ["paid", "paid_cash", "bypassed"].includes(s);
  const paidStatusLabel = (s) => {
    switch (s) {
      case "paid": return "Paid";
      case "paid_cash": return "Paid on Door";
      case "bypassed": return "Comped";
      case "approved": return "Awaiting payment";
      case "pending": return "Pending";
      default: return s;
    }
  };

  const selectedSubtotalCents = isEventReduce
    ? refundableTickets
        .filter((t) => selectedItemIds.includes(t.id) && isTicketPaid(t.status))
        .reduce((sum, t) => sum + (t.priceCents || 0), 0)
    : 0;

  return (
    <div className="gp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gp-modal-head">
          <div>
            <h3>{titleFor(action, target.type)}</h3>
            <p>{subtitleFor(action, target)}</p>
          </div>
          <button type="button" className="gp-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="gp-modal-body" ref={modalBodyRef}>
          {error && (
            <div
              className="gp-alert gp-alert-error"
              role="alert"
              style={{ position: 'sticky', top: 0, zIndex: 5, boxShadow: '0 4px 12px rgba(176,0,32,0.10)' }}
            >
              <i className="bi bi-exclamation-circle-fill"></i>
              <span>{error}</span>
            </div>
          )}

          {action === "cancel" && (
            <div className="gp-alert gp-alert-success" style={{ background: "#fff6db", color: "#7a5a00", borderColor: "#ffe69c" }}>
              <i className="bi bi-info-circle-fill"></i>
              <span>If your booking is within the refund window, the refund is processed automatically once approved by the venue.</span>
            </div>
          )}

          <form onSubmit={submit}>
            {isCountInput && (
              <div className="gp-form-row">
                {target.type === "reservation" && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "var(--gp-brand-tint)",
                      borderRadius: 8,
                      marginBottom: 12,
                      fontSize: ".9rem",
                    }}
                  >
                    <strong>You currently have {reservationCurrentGuests} {reservationCurrentGuests === 1 ? "guest" : "guests"}.</strong>{" "}
                    {action === "add_guests"
                      ? `After this request, your booking will be for ${reservationCurrentGuests + (Number(count) || 0)} ${(reservationCurrentGuests + (Number(count) || 0)) === 1 ? "guest" : "guests"}.`
                      : `After this request, your booking will be for ${Math.max(0, reservationCurrentGuests - (Number(count) || 0))} ${Math.max(0, reservationCurrentGuests - (Number(count) || 0)) === 1 ? "guest" : "guests"}.`}
                    {action === "reduce_guests" && (Number(count) || 0) >= reservationCurrentGuests && (
                      <div style={{ marginTop: 6, color: "var(--gp-danger)", fontSize: ".85rem" }}>
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        You can't remove everyone — use Cancel for that.
                      </div>
                    )}
                  </div>
                )}
                <label className="gp-label" htmlFor="gp-count">
                  How many {action === "add_guests" ? "to add" : "to remove"}?
                </label>
                <input
                  id="gp-count"
                  type="number"
                  min="1"
                  max={action === "reduce_guests" && target.type === "reservation" ? Math.max(1, reservationCurrentGuests - 1) : undefined}
                  className="gp-input"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  required
                />
              </div>
            )}

            {collectGuestData && (
              <div className="gp-form-row">
                <div
                  style={{
                    padding: "10px 12px",
                    background: "var(--gp-brand-tint)",
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: ".9rem",
                  }}
                >
                  <strong>You currently have {reservationCurrentGuests} {reservationCurrentGuests === 1 ? "guest" : "guests"}.</strong>{" "}
                  {`After this request, your booking will be for ${reservationCurrentGuests + resGuests.length} ${(reservationCurrentGuests + resGuests.length) === 1 ? "guest" : "guests"}.`}
                </div>
                <label className="gp-label" style={{ marginBottom: 4 }}>
                  Guests to add
                </label>
                <p className="gp-sub" style={{ marginBottom: 12, fontSize: ".85rem" }}>
                  This venue asks for each guest's details. Please enter who each
                  added guest is for.
                </p>
                <div className="gp-ticket-list">
                  {resGuests.map((g, idx) => (
                    <div
                      key={idx}
                      ref={(el) => (ticketRefs.current[idx] = el)}
                      className="gp-ticket"
                      style={{ display: "block", padding: 16 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <strong style={{ color: "var(--gp-brand-ink)" }}>
                          <i className="bi bi-person me-1"></i>
                          Guest #{idx + 1}
                        </strong>
                        {resGuests.length > 1 && (
                          <button
                            type="button"
                            className="gp-btn-text"
                            onClick={() => removeResGuestRow(idx)}
                            style={{ color: "var(--gp-danger)", padding: "4px 8px" }}
                            aria-label={`Remove guest #${idx + 1}`}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-rg-fn-${idx}`}>First name</label>
                          <input
                            id={`gp-rg-fn-${idx}`}
                            className="gp-input"
                            value={g.firstName}
                            onChange={(e) => updateResGuest(idx, { firstName: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-rg-ln-${idx}`}>Last name</label>
                          <input
                            id={`gp-rg-ln-${idx}`}
                            className="gp-input"
                            value={g.lastName}
                            onChange={(e) => updateResGuest(idx, { lastName: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-rg-em-${idx}`}>Email</label>
                          <input
                            id={`gp-rg-em-${idx}`}
                            type="email"
                            className="gp-input"
                            value={g.email}
                            onChange={(e) => updateResGuest(idx, { email: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label" htmlFor={`gp-rg-ph-${idx}`}>Phone</label>
                          <PhoneInput
                            country={"eg"}
                            value={g.phone}
                            onChange={(value) => updateResGuest(idx, { phone: value })}
                            inputProps={{
                              id: `gp-rg-ph-${idx}`,
                              autoComplete: "tel",
                            }}
                            inputClass="gp-input"
                            inputStyle={{ width: "100%", height: 46 }}
                            containerStyle={{ width: "100%" }}
                          />
                        </div>
                      </div>
                      {collectGuestSocial && (
                        <div style={{ marginTop: 10 }}>
                          <label
                            className={`gp-label${guestSocialRequired ? " gp-label-required" : ""}`}
                            htmlFor={`gp-rg-sm-${idx}`}
                          >
                            Social media link
                          </label>
                          <input
                            id={`gp-rg-sm-${idx}`}
                            className="gp-input"
                            value={g.socialMediaLink}
                            onChange={(e) => updateResGuest(idx, { socialMediaLink: e.target.value })}
                            placeholder="Instagram, Facebook, …"
                            required={guestSocialRequired}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="gp-btn-secondary"
                  onClick={addResGuestRow}
                  style={{ marginTop: 10, alignSelf: "flex-start" }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Add another guest
                </button>
              </div>
            )}

            {isEventAdd && (
              <div className="gp-form-row">
                <label className="gp-label" style={{ marginBottom: 4 }}>
                  Bookings to add
                </label>
                <p className="gp-sub" style={{ marginBottom: 12, fontSize: ".85rem" }}>
                  {allowSameAsBuyer
                    ? 'Enter who each booking is for, or check "Same as buyer" to use your own details.'
                    : "Enter who each booking is for."}
                </p>
                <div className="gp-ticket-list">
                  {tickets.map((t, idx) => {
                    const tt = availableTicketTypes.find((x) => x.id === t.ticketTypeId);
                    return (
                    <div
                      key={idx}
                      ref={(el) => (ticketRefs.current[idx] = el)}
                      className="gp-ticket"
                      style={{ display: "block", padding: 16 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <strong style={{ color: "var(--gp-brand-ink)" }}>
                          <i className="bi bi-ticket-perforated me-1"></i>
                          Booking #{idx + 1}
                          {tt?.priceCents != null && (
                            <span style={{ fontWeight: 500, color: "var(--gp-text-muted)", marginLeft: 8 }}>
                              · EGP {(tt.priceCents / 100).toFixed(2)}
                            </span>
                          )}
                        </strong>
                        {tickets.length > 1 && (
                          <button
                            type="button"
                            className="gp-btn-text"
                            onClick={() => removeTicketRow(idx)}
                            style={{ color: "var(--gp-danger)", padding: "4px 8px" }}
                            aria-label={`Remove booking #${idx + 1}`}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                      {/* Ticket type is locked to the order's existing type — all tickets in
                          one order must share the same type/price (enforced by the API). */}
                      {tt && (
                        <div style={{ marginBottom: 12 }}>
                          <label className="gp-label">Booking type</label>
                          <div
                            className="gp-input"
                            style={{
                              background: "var(--gp-brand-tint)",
                              borderColor: "var(--gp-border)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "default",
                            }}
                            title="All bookings in an experience must be the same type and price."
                          >
                            <strong>{tt.name}</strong>
                            {tt.priceCents != null && (
                              <span style={{ color: "var(--gp-brand-ink)", fontWeight: 600 }}>
                                EGP {(tt.priceCents / 100).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {allowSameAsBuyer && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 10px",
                            background: t.sameAsBuyer ? "var(--gp-brand-tint)" : "#fafafa",
                            border: "1px solid var(--gp-border)",
                            borderRadius: 8,
                            marginBottom: 12,
                            cursor: "pointer",
                            fontSize: ".88rem",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={t.sameAsBuyer}
                            onChange={() => toggleSameAsBuyer(idx)}
                            style={{ accentColor: "var(--gp-brand)" }}
                          />
                          <span>
                            <strong>Same as buyer</strong>
                            <span style={{ color: "var(--gp-text-muted)" }}>
                              {" "}({buyer.firstName} {buyer.lastName} · {buyer.email})
                            </span>
                          </span>
                        </label>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-fn-${idx}`}>First name</label>
                          <input
                            id={`gp-fn-${idx}`}
                            className="gp-input"
                            value={t.firstName}
                            onChange={(e) => updateTicket(idx, { firstName: e.target.value, sameAsBuyer: false })}
                            disabled={t.sameAsBuyer}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-ln-${idx}`}>Last name</label>
                          <input
                            id={`gp-ln-${idx}`}
                            className="gp-input"
                            value={t.lastName}
                            onChange={(e) => updateTicket(idx, { lastName: e.target.value, sameAsBuyer: false })}
                            disabled={t.sameAsBuyer}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-em-${idx}`}>Email</label>
                          <input
                            id={`gp-em-${idx}`}
                            type="email"
                            className="gp-input"
                            value={t.email}
                            onChange={(e) => updateTicket(idx, { email: e.target.value, sameAsBuyer: false })}
                            disabled={t.sameAsBuyer}
                            required
                          />
                        </div>
                        <div>
                          <label className="gp-label gp-label-required" htmlFor={`gp-ph-${idx}`}>Phone</label>
                          <PhoneInput
                            country={"eg"}
                            value={t.phone}
                            onChange={(value) => updateTicket(idx, { phone: value, sameAsBuyer: false })}
                            disabled={t.sameAsBuyer}
                            inputProps={{
                              id: `gp-ph-${idx}`,
                              required: true,
                              autoComplete: "tel",
                            }}
                            inputClass="gp-input"
                            inputStyle={{ width: "100%", height: 46 }}
                            containerStyle={{ width: "100%" }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
                <button
                  type="button"
                  className="gp-btn-secondary"
                  onClick={addTicketRow}
                  style={{ marginTop: 10, alignSelf: "flex-start" }}
                >
                  <i className="bi bi-plus-circle"></i>
                  Add another booking
                </button>
                {(() => {
                  const totalCents = tickets.reduce((sum, t) => {
                    const tt = availableTicketTypes.find((x) => x.id === t.ticketTypeId);
                    return sum + (tt?.priceCents || 0);
                  }, 0);
                  const afterCount = orderCurrentTickets + tickets.length;
                  return (
                    <>
                      <div
                        style={{
                          padding: "10px 12px",
                          background: "var(--gp-brand-tint)",
                          borderRadius: 8,
                          margin: "12px 0",
                          fontSize: ".9rem",
                        }}
                      >
                        <strong>You have {orderCurrentTickets} booking{orderCurrentTickets === 1 ? "" : "s"}.</strong>{" "}
                        After this request you'll have <strong>{afterCount}</strong>.
                      </div>
                      <div className="gp-summary-bar">
                        <span>
                          {tickets.length} booking{tickets.length === 1 ? "" : "s"} to add
                        </span>
                        <span>
                          {totalCents > 0
                            ? `+ EGP ${(totalCents / 100).toFixed(2)} (existing payment link will update)`
                            : "Existing payment link updates with the new total"}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {isReservationReschedule && (
              <div className="gp-form-row">
                {(() => {
                  const changed =
                    rescheduleDate !== currentReservationDateIso ||
                    rescheduleSlot !== currentReservationTime;
                  return (
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 10,
                        background: changed ? "var(--gp-brand-tint)" : "#f8f4fb",
                        border: "1px solid var(--gp-border)",
                        marginBottom: 14,
                        fontSize: ".9rem",
                      }}
                    >
                      <div>
                        <strong>Currently:</strong>{" "}
                        {currentReservationDateIso ? fmtDate(currentReservationDateIso) : "—"}
                        {currentReservationTime ? ` at ${fmtTime12h(currentReservationTime)}` : ""}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <strong>{changed ? "Changing to:" : "Pick a different date or time to change."}</strong>{" "}
                        {changed && rescheduleDate
                          ? `${fmtDate(rescheduleDate)}${rescheduleSlot ? ` at ${fmtTime12h(rescheduleSlot)}` : ""}`
                          : ""}
                      </div>
                    </div>
                  );
                })()}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <label className="gp-label" style={{ margin: 0 }}>
                    Select a date
                  </label>
                  <div className="gp-toggle" role="tablist" aria-label="Date view">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={dateMode === "quick"}
                      className={`gp-toggle-btn${dateMode === "quick" ? " is-active" : ""}`}
                      onClick={() => setDateMode("quick")}
                    >
                      Quick Date
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={dateMode === "calendar"}
                      className={`gp-toggle-btn${dateMode === "calendar" ? " is-active" : ""}`}
                      onClick={() => setDateMode("calendar")}
                    >
                      Calendar View
                    </button>
                  </div>
                </div>

                {dateMode === "quick" ? (
                  <div className="gp-scroller">
                    <button
                      type="button"
                      className="gp-scroller-arrow"
                      onClick={() => scrollByDir(dateScrollRef, -1)}
                      aria-label="Scroll dates left"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <div className="gp-scroller-track" ref={dateScrollRef}>
                      {dateOptions.map((d) => {
                        const selected = d.iso === rescheduleDate;
                        return (
                          <button
                            key={d.iso}
                            type="button"
                            data-date={d.iso}
                            className={`gp-date-pill${selected ? " is-selected" : ""}`}
                            onClick={() => setRescheduleDate(d.iso)}
                          >
                            <div className="gp-date-pill-weekday">{d.weekday}</div>
                            <div className="gp-date-pill-day">{d.day}</div>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      className="gp-scroller-arrow"
                      onClick={() => scrollByDir(dateScrollRef, 1)}
                      aria-label="Scroll dates right"
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                ) : (
                  <CalendarPicker
                    cursor={calendarCursor}
                    setCursor={setCalendarCursor}
                    selectedIso={rescheduleDate}
                    onSelect={(iso) => setRescheduleDate(iso)}
                    minIso={todayISO}
                  />
                )}

                {(() => {
                  if (!slotsData?.eligible || !Array.isArray(slotsData.shifts)) return null;
                  const visibleShifts = slotsData.shifts.filter((sh) => sh.showLabelToCustomers);
                  if (visibleShifts.length === 0) return null;
                  // Label-first selection (mirrors the booking widget): when shifts are
                  // labeled — e.g. "Breakfast" vs "Beach Access" running at the SAME time —
                  // the guest must pick the shift first; only that shift's times then show.
                  // A single labeled shift auto-selects.
                  const activeId =
                    rescheduleShiftId != null
                      ? rescheduleShiftId
                      : visibleShifts.length === 1
                        ? visibleShifts[0].id
                        : null;
                  return (
                    <div style={{ marginTop: 16 }}>
                      <label className="gp-label" style={{ marginBottom: 6 }}>
                        Choose an option
                      </label>
                      <div className="gp-shift-grid">
                        {visibleShifts.map((sh) => {
                          const selected = activeId === sh.id;
                          return (
                            <button
                              type="button"
                              key={sh.id}
                              className={`gp-shift-pill${selected ? " is-selected" : ""}`}
                              onClick={() => {
                                // Pick this shift; clear any time chosen under another
                                // shift so it isn't carried across.
                                setRescheduleShiftId(sh.id);
                                setRescheduleSlot(null);
                              }}
                            >
                              <span className="gp-shift-pill-name">{sh.label || `Shift #${sh.id}`}</span>
                              {sh.startTime && sh.endTime && (
                                <span className="gp-shift-pill-hours">
                                  {fmtTime12h(sh.startTime.substring(0, 5))} – {fmtTime12h(sh.endTime.substring(0, 5))}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  // Hide the "Select a time" heading until a labeled shift is chosen.
                  const visibleShifts = Array.isArray(slotsData?.shifts)
                    ? slotsData.shifts.filter((sh) => sh.showLabelToCustomers)
                    : [];
                  const activeId =
                    rescheduleShiftId != null
                      ? rescheduleShiftId
                      : visibleShifts.length === 1
                        ? visibleShifts[0].id
                        : null;
                  if (visibleShifts.length > 0 && activeId == null) return null;
                  return (
                    <>
                      <label className="gp-label" style={{ marginTop: 12, marginBottom: 6 }}>
                        Select a time
                      </label>
                      <p className="gp-sub" style={{ marginBottom: 10, marginTop: 0, fontSize: ".82rem" }}>
                        {slotsData?.allowsWaitlist
                          ? "Full slots can still be requested — you'll be on the waitlist if the venue approves."
                          : "Slot intervals and availability follow your venue's rules."}
                      </p>
                    </>
                  );
                })()}

                {slotsData === null && (
                  <div className="gp-empty" style={{ padding: 24 }}>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <div style={{ marginTop: 8 }}>Loading available times…</div>
                  </div>
                )}
                {slotsData?.error && (
                  <div className="gp-alert gp-alert-error">
                    <i className="bi bi-exclamation-circle-fill"></i>
                    <span>{slotsData.error}</span>
                  </div>
                )}
                {slotsData && !slotsData.error && !slotsData.eligible && (
                  <div className="gp-alert gp-alert-error">
                    <i className="bi bi-exclamation-circle-fill"></i>
                    <span>{slotsData.reason || "This shift doesn't run on the selected day."}</span>
                  </div>
                )}
                {(() => {
                  // Build the time strip. When shifts are labeled, only the chosen
                  // shift's slots show (label-first) so overlapping shifts never mix —
                  // a 12:00 "Breakfast" slot stays distinct from a 12:00 "Beach Access"
                  // one. When no shifts are labeled, merge them all (widget-style).
                  const allShifts =
                    slotsData?.eligible && Array.isArray(slotsData.shifts) ? slotsData.shifts : [];
                  const visibleShifts = allShifts.filter((sh) => sh.showLabelToCustomers);
                  const activeId =
                    rescheduleShiftId != null
                      ? rescheduleShiftId
                      : visibleShifts.length === 1
                        ? visibleShifts[0].id
                        : null;
                  // Labeled shifts present but none picked yet → prompt, no times.
                  if (visibleShifts.length > 0 && activeId == null) {
                    return (
                      <div className="gp-empty" style={{ padding: 24 }}>
                        <i className="bi bi-arrow-up-circle" style={{ fontSize: 28, color: "var(--gp-text-muted)" }}></i>
                        <div style={{ marginTop: 8 }}>Choose an option above to see the available times.</div>
                      </div>
                    );
                  }
                  const sourceShifts =
                    visibleShifts.length > 0
                      ? allShifts.filter((sh) => sh.id === activeId)
                      : allShifts;
                  const mergedSlots = [];
                  for (const sh of sourceShifts) {
                    for (const s of (sh.slots || [])) {
                      mergedSlots.push({ ...s, shiftId: sh.id, shiftAllowsWaitlist: sh.allowsWaitlist });
                    }
                  }
                  mergedSlots.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
                  if (slotsData && !slotsData.error && slotsData.eligible && mergedSlots.length === 0) {
                    return (
                      <div className="gp-empty" style={{ padding: 24 }}>
                        <i className="bi bi-calendar2-x" style={{ fontSize: 28, color: "var(--gp-text-muted)" }}></i>
                        <div style={{ marginTop: 8 }}>No time slots are configured for that date.</div>
                      </div>
                    );
                  }
                  if (!slotsData?.eligible || mergedSlots.length === 0) return null;
                  return (
                    <div className="gp-scroller">
                      <button
                        type="button"
                        className="gp-scroller-arrow"
                        onClick={() => scrollByDir(timeScrollRef, -1)}
                        aria-label="Scroll times left"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <div className="gp-scroller-track" ref={timeScrollRef}>
                        {mergedSlots.map((s) => {
                          const allowWaitlist = s.shiftAllowsWaitlist && s.isFull;
                          const disabled = s.isBlocked || s.isPast || (s.isFull && !allowWaitlist);
                          const slotKey = `${s.shiftId}-${s.time}`;
                          const isSelected = rescheduleSlot === s.time && (rescheduleShiftId == null || rescheduleShiftId === s.shiftId);
                          const subLabel = s.isBlocked
                            ? "Closed"
                            : s.isPast
                              ? "Past"
                              : s.isFull
                                ? (allowWaitlist ? "Waitlist" : "Sold out")
                                : null;
                          return (
                            <button
                              key={slotKey}
                              type="button"
                              className={`gp-time-pill${isSelected ? " is-selected" : ""}`}
                              disabled={disabled}
                              onClick={() => {
                                if (disabled) return;
                                setRescheduleSlot(s.time);
                                setRescheduleShiftId(s.shiftId);
                              }}
                              title={s.isBlocked ? "Closed" : s.isPast ? "This time has already passed" : s.isFull ? (allowWaitlist ? "Full — you'd be on the waitlist" : "Sold out for your party size") : ""}
                            >
                              <span>{fmtTime12h(s.time)}</span>
                              {subLabel && <span className="gp-time-pill-sub">{subLabel}</span>}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        className="gp-scroller-arrow"
                        onClick={() => scrollByDir(timeScrollRef, 1)}
                        aria-label="Scroll times right"
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  );
                })()}

                {rescheduleSlot && (() => {
                  const selShift = Array.isArray(slotsData?.shifts)
                    ? slotsData.shifts.find((s) => s.id === rescheduleShiftId)
                    : null;
                  return (
                    <div className="gp-summary-bar" style={{ marginTop: 14 }}>
                      <span>
                        Moving to <strong>{rescheduleDate}</strong> at <strong>{fmtTime12h(rescheduleSlot)}</strong>
                        {selShift?.label && <> · <strong>{selShift.label}</strong></>}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {isEventTransfer && (
              <div className="gp-form-row">
                <label className="gp-label" style={{ marginBottom: 4 }}>
                  Pick the experience you want to move to
                </label>
                <p className="gp-sub" style={{ marginBottom: 12, fontSize: ".85rem" }}>
                  Only experiences with the same booking types at the same price are listed. Your bookings keep their existing payment.
                </p>
                {transferTargets === null && (
                  <div className="gp-empty" style={{ padding: 24 }}>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <div style={{ marginTop: 8 }}>Loading available experiences…</div>
                  </div>
                )}
                {transferTargets?.error && (
                  <div className="gp-alert gp-alert-error">
                    <i className="bi bi-exclamation-circle-fill"></i>
                    <span>{transferTargets.error}</span>
                  </div>
                )}
                {transferTargets && !transferTargets.error && (transferTargets.candidates?.length ?? 0) === 0 && (
                  <div className="gp-empty" style={{ padding: 24 }}>
                    <i className="bi bi-calendar2-x" style={{ fontSize: 28, color: "var(--gp-text-muted)" }}></i>
                    <div style={{ marginTop: 8 }}>
                      No other experiences match this booking's types and prices right now.
                    </div>
                  </div>
                )}
                {transferTargets && !transferTargets.error && (transferTargets.candidates?.length ?? 0) > 0 && (
                  <div className="gp-ticket-list">
                    {transferTargets.candidates.map((ev) => {
                      const selected = selectedTargetEventId === ev.eventId;
                      return (
                        <label
                          key={ev.eventId}
                          className={`gp-ticket${selected ? " is-selected" : ""}`}
                          style={{ alignItems: "center" }}
                        >
                          <input
                            type="radio"
                            name="transferTarget"
                            checked={selected}
                            onChange={() => setSelectedTargetEventId(ev.eventId)}
                            style={{ accentColor: "var(--gp-brand)" }}
                          />
                          <div>
                            <div className="gp-ticket-name">{ev.eventName}</div>
                            <div className="gp-ticket-meta">
                              <span><i className="bi bi-calendar3"></i>{fmtDate(ev.eventDate)}</span>
                              {ev.startTime && <span><i className="bi bi-clock"></i>{ev.startTime}</span>}
                              <span><i className="bi bi-ticket"></i>{(ev.matches || []).map((m) => m.targetName).join(", ")}</span>
                            </div>
                            <div className="gp-ticket-trust">
                              <i className="bi bi-shield-check"></i>
                              Same booking types, same prices — your payment carries over
                            </div>
                          </div>
                          <div className="gp-ticket-price">
                            <i className="bi bi-check-circle-fill" style={{ color: "var(--gp-success)" }}></i>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {isEventEdit && (
              <div className="gp-form-row">
                <label className="gp-label" style={{ marginBottom: 4 }}>
                  Edit booking holder details
                </label>
                <p className="gp-sub" style={{ marginBottom: 12, fontSize: ".85rem" }}>
                  Update any of your bookings' name, email or phone. Only changed bookings are sent for review.
                </p>
                {editTickets.length === 0 ? (
                  <div className="gp-empty" style={{ padding: 24 }}>
                    <i className="bi bi-ticket-perforated" style={{ fontSize: 28, color: "var(--gp-text-muted)" }}></i>
                    <div style={{ marginTop: 8 }}>No bookings available to edit.</div>
                  </div>
                ) : (
                  <div className="gp-ticket-list">
                    {editTickets.map((t, idx) => {
                      const isChanged =
                        t.firstName !== t.original.firstName ||
                        t.lastName !== t.original.lastName ||
                        t.email !== t.original.email ||
                        t.phone !== t.original.phone;
                      return (
                        <div
                          key={t.itemId}
                          className={`gp-ticket${isChanged ? " is-selected" : ""}`}
                          style={{ display: "block", padding: 18 }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <strong style={{ color: "var(--gp-brand-ink)" }}>
                              <i className="bi bi-ticket-perforated me-1"></i>
                              Booking #{idx + 1}
                              {t.ticketTypeName && (
                                <span style={{ fontWeight: 500, color: "var(--gp-text-muted)", marginLeft: 8 }}>
                                  · {t.ticketTypeName}
                                </span>
                              )}
                            </strong>
                            {isChanged && (
                              <span className="gp-badge gp-badge-info">
                                <i className="bi bi-pencil-square"></i>
                                Modified
                              </span>
                            )}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                              <label className="gp-label gp-label-required" htmlFor={`gp-edit-fn-${idx}`}>First name</label>
                              <input
                                id={`gp-edit-fn-${idx}`}
                                className="gp-input"
                                value={t.firstName}
                                onChange={(e) => updateEditTicket(idx, { firstName: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <label className="gp-label gp-label-required" htmlFor={`gp-edit-ln-${idx}`}>Last name</label>
                              <input
                                id={`gp-edit-ln-${idx}`}
                                className="gp-input"
                                value={t.lastName}
                                onChange={(e) => updateEditTicket(idx, { lastName: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <label className="gp-label gp-label-required" htmlFor={`gp-edit-em-${idx}`}>Email</label>
                              <input
                                id={`gp-edit-em-${idx}`}
                                type="email"
                                className="gp-input"
                                value={t.email}
                                onChange={(e) => updateEditTicket(idx, { email: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <label className="gp-label gp-label-required" htmlFor={`gp-edit-ph-${idx}`}>Phone</label>
                              <PhoneInput
                                country={"eg"}
                                value={t.phone}
                                onChange={(value) => updateEditTicket(idx, { phone: value })}
                                inputProps={{
                                  id: `gp-edit-ph-${idx}`,
                                  required: true,
                                  autoComplete: "tel",
                                }}
                                inputClass="gp-input"
                                inputStyle={{ width: "100%", height: 46 }}
                                containerStyle={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {editTickets.some((t) =>
                  t.firstName !== t.original.firstName ||
                  t.lastName !== t.original.lastName ||
                  t.email !== t.original.email ||
                  t.phone !== t.original.phone,
                ) && (
                  <div className="gp-summary-bar" style={{ marginTop: 12 }}>
                    <span>
                      {editTickets.filter((t) =>
                        t.firstName !== t.original.firstName ||
                        t.lastName !== t.original.lastName ||
                        t.email !== t.original.email ||
                        t.phone !== t.original.phone,
                      ).length} booking{editTickets.length === 1 ? "" : "s"} modified
                    </span>
                    <span>
                      <i className="bi bi-info-circle me-1"></i>
                      Changes apply after the venue reviews
                    </span>
                  </div>
                )}
              </div>
            )}

            {isEventReduce && (
              <div className="gp-form-row">
                <label className="gp-label">Select the bookings you want to remove</label>
                {refundableTickets.length === 0 ? (
                  <div className="gp-empty" style={{ padding: 24 }}>
                    <i className="bi bi-ticket-perforated" style={{ fontSize: 28, color: "var(--gp-text-muted)" }}></i>
                    <div style={{ marginTop: 8 }}>No bookings are available to remove.</div>
                  </div>
                ) : (
                  <>
                    <div className="gp-ticket-list">
                      {refundableTickets.map((it) => {
                        const checked = selectedItemIds.includes(it.id);
                        const paid = isTicketPaid(it.status);
                        return (
                          <label key={it.id} className={`gp-ticket${checked ? " is-selected" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTicket(it.id)}
                            />
                            <div>
                              <div className="gp-ticket-name">
                                {it.firstName} {it.lastName}
                                {it.ticketType?.name && (
                                  <span style={{ fontWeight: 500, color: "var(--gp-text-muted)" }}>
                                    {" "}· {it.ticketType.name}
                                  </span>
                                )}
                              </div>
                              <div className="gp-ticket-meta">
                                <span>{paidStatusLabel(it.status)}</span>
                                {it.priceCents != null && (
                                  <span>EGP {(it.priceCents / 100).toFixed(2)}</span>
                                )}
                              </div>
                              {paid && (
                                <div className="gp-ticket-trust">
                                  <i className="bi bi-shield-check"></i>
                                  Refund will go back to the original payer
                                  {it.payerName && it.payerEmail !== target.item.buyerEmail
                                    ? ` (${it.payerName})`
                                    : ""}
                                </div>
                              )}
                            </div>
                            <div className="gp-ticket-price">
                              {it.priceCents != null ? `EGP ${(it.priceCents / 100).toFixed(2)}` : ""}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {selectedItemIds.length > 0 && (() => {
                      const remaining = orderCurrentTickets - selectedItemIds.length;
                      const removingAll = selectedItemIds.length >= orderCurrentTickets;
                      // Per-type min-per-order check (mirrors submit validation).
                      const remainingByType = new Map();
                      for (const it of orderActiveItems) {
                        if (selectedItemIds.includes(it.id)) continue;
                        const tid = it.ticketType?.id ?? it.ticketTypeId;
                        remainingByType.set(tid, (remainingByType.get(tid) || 0) + 1);
                      }
                      let belowMinWarn = null;
                      const seen = new Set();
                      for (const it of orderActiveItems) {
                        const tt = it.ticketType;
                        if (!tt || seen.has(tt.id)) continue;
                        seen.add(tt.id);
                        const r = remainingByType.get(tt.id) || 0;
                        const m = Number(tt.minPerOrder) || 0;
                        if (r > 0 && r < m) {
                          belowMinWarn = `"${tt.name}" requires a minimum of ${m} per experience — you'd be left with ${r}.`;
                          break;
                        }
                      }
                      return (
                        <>
                          <div className="gp-summary-bar">
                            <span>
                              {selectedItemIds.length} booking{selectedItemIds.length === 1 ? "" : "s"} selected · {remaining} remaining
                            </span>
                            <span>
                              {selectedSubtotalCents > 0
                                ? `Eligible refund: EGP ${(selectedSubtotalCents / 100).toFixed(2)}`
                                : "No refund (unpaid)"}
                            </span>
                          </div>
                          {removingAll && (
                            <div
                              className="gp-alert gp-alert-error"
                              style={{ marginTop: 10 }}
                            >
                              <i className="bi bi-exclamation-triangle-fill"></i>
                              <span>You can't remove all bookings — to do that, please cancel the booking instead.</span>
                            </div>
                          )}
                          {!removingAll && belowMinWarn && (
                            <div
                              className="gp-alert gp-alert-error"
                              style={{ marginTop: 10 }}
                            >
                              <i className="bi bi-exclamation-triangle-fill"></i>
                              <span>{belowMinWarn}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {isOther && (
              <div className="gp-form-row">
                <label className="gp-label gp-label-required" htmlFor="gp-other-message">
                  Your message
                </label>
                <p className="gp-sub" style={{ marginBottom: 8, fontSize: ".85rem" }}>
                  Tell the venue what you need. They'll review it and reply to you here.
                </p>
                <textarea
                  id="gp-other-message"
                  className="gp-textarea"
                  rows={5}
                  value={otherMessage}
                  onChange={(e) => setOtherMessage(e.target.value)}
                  placeholder="Type your message to the venue…"
                  maxLength={2000}
                  required
                  autoFocus
                />
              </div>
            )}

            {!isOther && (
              <div className="gp-form-row">
                <label className="gp-label" htmlFor="gp-notes">
                  Notes (optional)
                </label>
                <textarea
                  id="gp-notes"
                  className="gp-textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything the venue should know?"
                />
              </div>
            )}
          </form>
        </div>
        <div className="gp-modal-foot">
          <button type="button" className="gp-btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="gp-btn-primary" onClick={submit} disabled={busy}>
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Submitting…
              </>
            ) : (
              <>
                Submit request
                <i className="bi bi-arrow-right"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function titleFor(action, targetType) {
  const isEvent = targetType === "event_order";
  switch (action) {
    case "cancel": return "Cancel booking";
    case "add_guests": return isEvent ? "Add bookings" : "Add guests";
    case "reduce_guests": return isEvent ? "Reduce bookings" : "Reduce guests";
    case "edit": return "Edit booking data";
    case "transfer": return "Move to another experience";
    case "reschedule": return "Change reservation date";
    case "other": return "Send a message to the venue";
    default: return "Request change";
  }
}

function subtitleFor(action, target) {
  const where = target.type === "reservation" ? "reservation" : "experience booking";
  switch (action) {
    case "cancel":
      return `We’ll review your cancellation request and process any refund you’re entitled to.`;
    case "add_guests":
      return target.type === "event_order"
        ? `Extra bookings will be added to your existing payment link, no new link needed.`
        : `Need more seats on your ${where}? Let us know how many.`;
    case "reduce_guests":
      return "";
    case "edit":
      return target.type === "event_order"
        ? `Update name, email or phone on any of your bookings. Changes apply after the venue reviews.`
        : `Tell us what to change about your ${where}.`;
    case "transfer":
      return "";
    case "other":
      return "Have a question or a special request? Send the venue a message and they'll reply to you here.";
    default:
      return "";
  }
}

function CalendarPicker({ cursor, setCursor, selectedIso, onSelect, minIso }) {
  const { year, month } = cursor;
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const startDow = firstOfMonth.getDay(); // 0 = Sun
  const daysInMonth = lastOfMonth.getDate();
  const minDate = minIso ? new Date(minIso + "T00:00:00") : null;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = firstOfMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const goto = (delta) => {
    const next = new Date(year, month + delta, 1);
    setCursor({ year: next.getFullYear(), month: next.getMonth() });
  };
  const isoFor = (d) => {
    const y = year;
    const m = String(month + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  return (
    <div className="gp-calendar">
      <div className="gp-cal-head">
        <button type="button" className="gp-scroller-arrow" onClick={() => goto(-1)} aria-label="Previous month">
          <i className="bi bi-chevron-left"></i>
        </button>
        <div className="gp-cal-head-title">{monthLabel}</div>
        <button type="button" className="gp-scroller-arrow" onClick={() => goto(1)} aria-label="Next month">
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>
      <div className="gp-cal-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <div key={w} className="gp-cal-dow">{w}</div>
        ))}
        {cells.map((d, idx) => {
          if (d == null) return <div key={`b-${idx}`} className="gp-cal-cell is-other-month" />;
          const iso = isoFor(d);
          const dateObj = new Date(iso + "T00:00:00");
          const disabled = minDate && dateObj < minDate;
          const selected = iso === selectedIso;
          return (
            <button
              key={iso}
              type="button"
              className={`gp-cal-cell${selected ? " is-selected" : ""}`}
              disabled={disabled}
              onClick={() => onSelect(iso)}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function fmtTime12h(t) {
  if (!t) return "";
  const [hStr, mStr = "00"] = String(t).split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return String(t);
  const period = h >= 12 ? "pm" : "am";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${mStr.padStart(2, "0")} ${period}`;
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d).split("T")[0];
    return dt.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}
